import React, { useMemo } from 'react';
import { ThreeDBarCanvas } from './ThreeDBarCanvas';
import { ThreeDBarConfig, ThreeDBarDatum, ThreeDBarDataMapping } from './config/threeDBarTypes';
import { DEFAULT_3D_BAR_CONFIG } from './config/threeDBarDefaults';
import { useThreeDBarData } from './hooks/useThreeDBarData';
import { COLOR_PALETTES, DEFAULT_PALETTE } from '../../../constants';

interface ThreeDBarChartProps {
    data: any[];
    config: any; // Parent chart config
    onConfigChange?: (updates: any) => void;
}

export const ThreeDBarChart: React.FC<ThreeDBarChartProps> = ({ data, config, onConfigChange }) => {
    // Merge parent config with 3D defaults
    const threeDConfig: ThreeDBarConfig = useMemo(() => ({
        ...DEFAULT_3D_BAR_CONFIG,
        ...(config.advanced?.threeDBar || {}),
        showGrid: config.customization?.showGrid ?? DEFAULT_3D_BAR_CONFIG.showGrid,
        showValueLabels: config.customization?.showDataLabels ?? DEFAULT_3D_BAR_CONFIG.showValueLabels
    }), [config.advanced?.threeDBar, config.customization]);

    // Map parent config fields to 3D mapping
    const mapping: ThreeDBarDataMapping = useMemo(() => ({
        xField: config.xField || '',
        zField: threeDConfig.zField || config.zField, // Support both locations
        seriesField: config.seriesField, // If exists in parent
        valueField: config.yField || config.valueField || '',
        colorField: config.colorField,
    }), [config, threeDConfig]);

    const palette = config.customization?.useMultipleColors
        ? (config.customization.paletteName === 'custom'
            ? config.customization.customPalette
            : COLOR_PALETTES[config.customization.paletteName || DEFAULT_PALETTE])
        : [config.customization?.color || '#4f46e5'];

    const { processedData, xCategories, zCategories, seriesNames, yScale } = useThreeDBarData(data, mapping, threeDConfig, palette);

    return (
        <div className="w-full h-full flex flex-col relative">
            {/* Canvas */}
            <div className="flex-1 min-h-0">
                <ThreeDBarCanvas
                    data={processedData}
                    config={threeDConfig}
                    xCategories={xCategories}
                    zCategories={zCategories}
                    seriesNames={seriesNames}
                    yScale={yScale}
                />
            </div>

            {/* Legend could go here */}
        </div>
    );
};
